// ---------------------------------------------------------------------------
// flow-verification — derives a flow's verification status from preview
// ActionFlow trace runs (F6). Pure selector feeding VerificationStatusBadge,
// plus the RUX-05 invalidation hook the edit paths call.
// ---------------------------------------------------------------------------

import type { ActionFlowTraceEntry } from '@/features/preview/types'
import { usePreviewTestingStore } from '@/features/preview/store/preview-testing-store'
import { flowNamesMatch } from '../services/flow-ownership'

export type FlowVerificationStatus = 'pass' | 'fail' | 'not_run'

/** Trace↔flow correlation: the ownership full-then-bare rule in BOTH
 *  directions — either side may be the more namespaced one. */
function traceMatchesFlow(flowName: string, trace: ActionFlowTraceEntry): boolean {
  return flowNamesMatch(flowName, trace.flowName) || flowNamesMatch(trace.flowName, flowName)
}

/**
 * Latest preview trace verdict for a flow.
 *
 * `traces` is expected in the preview-testing store's NEWEST-FIRST order
 * (addActionFlowTrace prepends), so the first matching entry IS the latest
 * run. Name correlation uses the ownership full-then-bare rule
 * (flowNamesMatch) in BOTH directions — the preview trace flowName may be
 * more or less namespaced than the editor flow name (e.g. an editor
 * 'store-nav.addToCart' runs as the block's bare 'addToCart' declaration
 * and vice versa) — keeping the badge consistent with save routing.
 *
 * Dry-run traces (source: 'dry-run', from the editor's Run-(dry) button) are
 * IGNORED here: an off-canvas run with an empty mock context proves the flow
 * executes, not that it works "verified in preview" against real UI inputs.
 * Letting a dry-run flip the badge to 'Verified' would overclaim. The dry-run
 * result still surfaces in the overlay/results panel — it just doesn't grant
 * the preview-verification badge. The badge stays 'not_run' until a real
 * preview run records a 'preview' (or unmarked legacy) trace.
 */
export function deriveFlowVerification(
  flowName: string,
  traces: ActionFlowTraceEntry[],
): FlowVerificationStatus {
  const latest = traces.find(
    (trace) => trace.source !== 'dry-run' && traceMatchesFlow(flowName, trace),
  )
  if (!latest) return 'not_run'
  return latest.status === 'pass' ? 'pass' : 'fail'
}

/**
 * RUX-05 — trace invalidation on edit. Editing, renaming, or deleting a flow
 * makes every recorded trace for it describe code that no longer exists, so
 * the matching traces are dropped and deriveFlowVerification honestly
 * returns 'not_run' until the next Preview run — a 'Verified' badge must
 * never outlive the steps it verified. Matching mirrors
 * deriveFlowVerification's bidirectional rule.
 */
export function invalidateFlowVerification(flowName: string): void {
  usePreviewTestingStore
    .getState()
    .removeActionFlowTraces((trace) => traceMatchesFlow(flowName, trace))
}
